import {Component, inject, input, OnInit, signal} from '@angular/core';
import {BaseComponent} from "../../../../shared/base/base-component";
import {ApiService} from "../../../../core/api/api.service";
import {
  type ActiveCompanyStatusResponse,
  BusinessSubTypeListResponse,
  GetBusinessSubTypeListParams,
  GetBusinessTypeListItem,
  GetBusinessTypeListParams,
  GetBusinessTypeListResponse,
  GetCompanyDetailParams,
  GetCompanyDetailResponse,
  GetCompanyDetailType,
  GetCompanyTypeListResponse,
  InactiveCompanyStatusResponse,
  UpdateBusinessAddressRequest,
  UpdateBusinessAddressResponse,
  UpdateBusinessInfoRequest,
  UpdateBusinessInfoResponse,
  UpdateGeneralInfoRequest,
  UpdateGeneralInfoResponse
} from "../../../../core/api/model";
import {State} from '../../../../shared/base/base-state';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {DropdownComponent} from "../../../../shared/inputs/dropdown/dropdown.component";
import {RadioButtonComponent} from "../../../../shared/inputs/radio-button/radio-button.component";
import {
  MultiSelectionFieldComponent
} from "../../../../shared/inputs/multi-selection-field/multi-selection-field.component";
import {LookupService} from "../../../../core/services/lookup.service";
import {CarrierTypeResponse} from "../../../../core/services/lookup/carrier-type-response";
import {AccountType, AccountTypesResponse} from "../../../../core/services/lookup/account-type-response";
import {NgClass} from "@angular/common";
import {OverlayService} from "../../../../shared/components/overlay/overlay.service";
import {CommonService} from "../../../../core/services/common/common.services";
import {CountriesResponse, Country, CountryState} from "../../../../core/services/common/countries/countries-response";
import {TextInputComponent} from "../../../../shared/inputs/text-input/text-input.component";
import {ButtonComponent} from "../../../../shared/components/button/button.component";

@Component({
  selector: 'app-company-details',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    DropdownComponent,
    RadioButtonComponent,
    MultiSelectionFieldComponent,
    NgClass,
    TextInputComponent,
    ButtonComponent,
  ],
  templateUrl: './company-details.component.html',
  standalone: true,
  styleUrl: './company-details.component.scss'
})
export class CompanyDetailsComponent extends BaseComponent implements OnInit {

  id = input<string>();

  apiService = inject(ApiService);
  lookupService = inject(LookupService);
  getCompanyByIdState = new State<GetCompanyDetailResponse>();
  formBuilder = inject(FormBuilder);
  overlayService = inject(OverlayService);
  commonService = inject(CommonService);

  generalInformationForm!: FormGroup;
  businessAddressForm!: FormGroup;
  businessInformationForm!: FormGroup;

  accountTypeState = new State<AccountTypesResponse>();
  carrierTypeState = new State<CarrierTypeResponse>();
  countryListState = new State<CountriesResponse>();
  companyTypesState = new State<GetCompanyTypeListResponse>();
  businessTypeState = new State<GetBusinessTypeListResponse>();
  businessSubTypeState = new State<BusinessSubTypeListResponse>();
  updateGeneralInfoState = new State<UpdateGeneralInfoResponse>();
  updateBusinessAddressState = new State<UpdateBusinessAddressResponse>();
  updateBusinessInformationState = new State<UpdateBusinessInfoResponse>();
  enableCompanyState = new State<ActiveCompanyStatusResponse>();
  disableCompanyState = new State<InactiveCompanyStatusResponse>();

  statesList: CountryState[] = [];

  selectedAccountType: string | null | undefined;
  isEnableCompany = signal(true);
  isInterStateSelected = signal<boolean>(false);
  isIntraStateSelected = signal<boolean>(false);

  isEditBasicDetails = signal(false);
  isAddressEditable = signal(false);
  isBusinessInformationEditable = signal(false);

  ngOnInit() {
    this.initGeneralInformationForm();
    this.initBusinessAddressForm();
    this.initBusinessInformationForm();
    this.getCompanyTypes();
    this.getAccountTypes();
    this.getCountries();
    this.getBusinessTypes();
    this.getBusinessSubTypes();
    this.getCarrierTypes();
    this.getCompanyById();
  }

  initGeneralInformationForm() {
    this.generalInformationForm = this.formBuilder.group({
      companyName: [null, Validators.required],
      accountType: [null, Validators.required],
      companyPrefix: [null, Validators.required],
      einNumber: [null, Validators.required],
      email: [null, Validators.required],
      dba: [null, Validators.required],
      phoneNumber: [null, Validators.required],
      companyStatus: [null, Validators.required],
    });
    this.generalInformationForm.disable();
  }

  initBusinessAddressForm() {
    this.businessAddressForm = this.formBuilder.group({
      companyType: [null, Validators.required],
      addressLine1: [null, Validators.required],
      addressLine2: [null, Validators.required],
      city: [null, Validators.required],
      stateId: [null, Validators.required],
      country: [null, Validators.required],
      applicationDate: [null, Validators.required],
      zipCode: [null, Validators.required],
    });
    this.businessAddressForm.disable();
  }

  initBusinessInformationForm() {
    this.businessInformationForm = this.formBuilder.group({
      businessTypeId: [null, Validators.required],
      businessSubTypeId: [null],
      businessRepresentativeFirstName: [null, Validators.required],
      businessRepresentativeLastName: [null, Validators.required],
      businessRepresentativeEmail: [null, Validators.required],
      businessRepPhoneNumber: [null, Validators.required],
      carrierType: [null, Validators.required],
      dotNumber: [null, Validators.required],
      mcNumber: [null, Validators.required],
      caNumber: [null, Validators.required],
      transportsHazmat: [false, Validators.required],
      transportsHazwaste: [false, Validators.required],
      email: [null, Validators.required]
    });
    this.businessInformationForm.disable();
  }

  getCompanyById() {
    const request: GetCompanyDetailParams = {
      id: this.id() ?? '',
      type: GetCompanyDetailType.nextDays,
      value: '10'
    };

    this.executeRequest({
      state: this.getCompanyByIdState,
      request: this.apiService.getCompanyDetail(request),
      onSuccess: (response) => {

        const data = response.data;
        let selectedCountry = this.countryListState.response()?.data.find(c => c.id === data.countryId) ?? null;
        this.onCountrySelected(selectedCountry);

        let status = (data.companyStatus ?? '').trim().toLowerCase();

        if (status === 'active') {
          this.isEnableCompany.set(false);
        } else {
          this.isEnableCompany.set(true);
        }

        this.generalInformationForm.patchValue({
          companyName: data.companyName ?? '',
          accountType: data.accountType ?? '',
          companyPrefix: data.prefix ?? '',
          einNumber: data.einNumber ?? '',
          email: data.email ?? '',
          dba: data.dba ?? '',
          phoneNumber: data.phoneNumber ?? '',
          companyStatus: data.companyStatus ?? ''
        });

        this.businessAddressForm.patchValue({
          companyType: data.companyType ?? '',
          addressLine1: data.addressLine1 ?? '',
          addressLine2: data.addressLine2 ?? '',
          city: data.city ?? '',
          stateId: data.stateId ?? '',
          country: data.countryId ?? '',
          zipCode: data.zipCode ?? '',
        });

        this.businessInformationForm.patchValue({
          businessTypeId: data.businessTypeId ?? '',
          businessSubTypeId: data.businessSubTypeId ?? null,
          businessRepresentativeFirstName: data.businessRepFirstName ?? '',
          businessRepresentativeLastName: data.businessRepLastName ?? '',
          businessRepresentativeEmail: data.businessRepEmail ?? '',
          businessRepPhoneNumber: data.businessRepPhoneNumber ?? '',
          dotNumber: data.dotNumber ?? '',
          mcNumber: data.mcNumber ?? '',
          caNumber: data.caNumber ?? '',
          transportsHazmat: data.transportsHazmat ?? false,
          transportsHazwaste: data.transportsHazwaste ?? false,
          email: data.email ?? ''
        });

        const carrierTypeValue = [];
        if (data.isInterState) {
          carrierTypeValue.push('INTERSTATE');
          this.isInterStateSelected.set(true);
        }
        if (data.isIntraState) {
          carrierTypeValue.push('INTRASTATE');
          this.isIntraStateSelected.set(true);
        }

        this.businessInformationForm.controls['carrierType'].setValue(carrierTypeValue);
        this.onSelectedCarrierChanged(carrierTypeValue);

        this.generalInformationForm.disable();
        this.businessAddressForm.disable();
        this.businessInformationForm.disable();
      }
    });
  }

  onEnableCompanyClicked() {
    let dialog = this.overlayService.openAlert('Enable Company', "Are you sure you want to enable this company?");
    dialog.closed.subscribe((result) => {
      if (result) {
        let request = {id: this.id() ?? ''};

        this.executeRequest({
          state: this.enableCompanyState,
          request: this.apiService.activeCompanyStatus(request),
          handleSuccess: true,
          handleError: true,
          onSuccess: (response) => {
            this.isEnableCompany.set(false);
          }
        });
      }
    });
  }

  onDisableCompanyClicked() {
    let dialog = this.overlayService.openAlert('Disable Company', "Are you sure you want to disable this company?");
    dialog.closed.subscribe((result) => {
      if (result) {
        let request = {id: this.id() ?? ''};
        this.executeRequest({
          state: this.disableCompanyState,
          request: this.apiService.inActiveCompanyStatus(request),
          handleSuccess: true,
          handleError: true,
          onSuccess: (response) => {
            this.isEnableCompany.set(true);
          }
        });
      }
    });
  }

  onUpdateGeneralInfoClicked() {
    const formValue = this.generalInformationForm.getRawValue();
    const request: UpdateGeneralInfoRequest = {
      accountType: formValue.accountType,
      companyName: formValue.companyName,
      dba: formValue.dba,
      einNumber: formValue.einNumber,
      ownerSsnNumber: formValue.ownerSsnNumber,
      phoneNumber: formValue.phoneNumber,
      prefix: formValue.companyPrefix
    };
    this.executeRequest({
      state: this.updateGeneralInfoState,
      request: this.apiService.updateGeneralInfo(this.id()!, request)
    });
  }

  onUpdateBusinessAddressClicked() {
    const formValue = this.businessAddressForm.getRawValue();
    const request: UpdateBusinessAddressRequest = {
      addressLine1: formValue.addressLine1,
      addressLine2: formValue.addressLine2,
      city: formValue.city,
      companyType: formValue.companyType,
      countryId: formValue.country,
      stateId: formValue.stateId,
      zipCode: formValue.zipCode,
    };
    this.executeRequest({
      state: this.updateBusinessAddressState,
      request: this.apiService.updateBusinessAddress(this.id()!, request),
      handleSuccess: true,
      handleError: true,
    });
  }

  onUpdateBusinessInformationClicked() {
    const formValue = this.businessInformationForm.getRawValue();
    const request: UpdateBusinessInfoRequest = {
      businessRepresentativeFirstName: formValue.businessRepresentativeFirstName,
      businessRepresentativeLastName: formValue.businessRepresentativeLastName,
      businessRepresentativeEmail: formValue.businessRepresentativeEmail,
      businessRepPhoneNumber: formValue.businessRepPhoneNumber,
      businessTypeId: formValue.businessTypeId,
      businessSubTypeId: formValue.businessSubTypeId,
      caNumber: formValue.caNumber,
      dotNumber: formValue.dotNumber,
      isInterState: this.isInterStateSelected(),
      isIntraState: this.isIntraStateSelected(),
      mcNumber: formValue.mcNumber,
      transportsHazmat: formValue.transportsHazmat,
      transportsHazwaste: formValue.transportsHazwaste
    };
    this.executeRequest({
      state: this.updateBusinessInformationState,
      request: this.apiService.updateBusinessInfo(this.id()!, request),
      handleSuccess: true,
      handleError: true
    });
  }

  onEditBasicDetailsClicked() {
    this.isEditBasicDetails.set(true);
    this.generalInformationForm.controls['accountType'].enable();
    this.generalInformationForm.controls['einNumber'].enable();
    this.generalInformationForm.controls['dba'].enable();
    this.generalInformationForm.controls['phoneNumber'].enable();
    this.generalInformationForm.controls['companyStatus'].enable();
  }

  onEditAddressDetailsClicked() {
    this.isAddressEditable.set(true);
    Object.values(this.businessAddressForm.controls).forEach(control => control.enable());
  }

  onEditBusinessInformationClicked() {
    this.isBusinessInformationEditable.set(true);
    Object.values(this.businessInformationForm.controls).forEach(control => control.enable());

    const selectedCarrierType = this.businessInformationForm.get('carrierType')?.value;
    if (selectedCarrierType?.length) {
      this.onSelectedCarrierChanged(selectedCarrierType);
    }
  }

  onCancelGeneralInformationClicked() {
    this.isEditBasicDetails.set(false);
    this.generalInformationForm.disable();
  }

  onCancelBusinessAddressClicked() {
    this.isAddressEditable.set(false);
    this.businessAddressForm.disable();
  }

  onCancelBusinessInformationClicked() {
    this.isBusinessInformationEditable.set(false);
    this.businessInformationForm.disable();
  }

  getCompanyTypes() {
    this.executeRequest({
      state: this.companyTypesState,
      request: this.apiService.getCompanyTypeList(),
    });
  }

  getAccountTypes() {
    this.executeRequest({
      state: this.accountTypeState,
      request: this.lookupService.getAccountTypes(),
    });
  }

  getCountries() {
    this.executeRequest<CountriesResponse>({
      state: this.countryListState,
      request: this.commonService.getCountries(),
    });
  }


  getBusinessTypes() {
    const request: GetBusinessTypeListParams = {
      pageNumber: 1,
      pageSize: 100,
    };
    this.executeRequest({
      state: this.businessTypeState,
      request: this.apiService.getBusinessTypeList(request),
    });
  }

  getBusinessSubTypes() {
    const request: GetBusinessSubTypeListParams = {
      pageNumber: 1,
      pageSize: 100,
    };
    this.executeRequest({
      state: this.businessSubTypeState,
      request: this.apiService.getBusinessSubTypeList(request),
    });
  }

  getCarrierTypes() {
    this.executeRequest({
      state: this.carrierTypeState,
      request: this.lookupService.getCarrierType(),
    });
  }

  onBusinessTypeChanged($event: GetBusinessTypeListItem | null) {
    if ($event?.name != 'Bus') {
      this.businessInformationForm.controls["businessSubTypeId"].setValue(null);
    }
  }

  onCountrySelected(selectedItem: Country | null) {
    this.businessAddressForm.controls["stateId"].patchValue({stateId: null});
    this.statesList = selectedItem?.states ?? [];
  }

  onAccountTypeChanged(selectedItem: AccountType | null) {
    this.selectedAccountType = selectedItem?.name;
    this.handleSSnAndEinNumber(selectedItem?.name);
  }

  handleSSnAndEinNumber(accountType: string | undefined) {
    switch (accountType) {
      case "Individual":
        this.businessInformationForm.controls["ownerSsnNumber"].enable();
        this.businessInformationForm.controls["einNumber"].disable();
        break;
      case "Company":
        this.businessInformationForm.controls["einNumber"].enable();
        this.businessInformationForm.controls["ownerSsnNumber"].disable();
        break;
    }
  }

  onSelectedCarrierChanged(selectedItems: any[] | null) {
    let selectedCarrierTypes = selectedItems ?? [];

    const isInter = selectedCarrierTypes.includes('INTERSTATE');
    const isIntra = selectedCarrierTypes.includes('INTRASTATE');

    this.isInterStateSelected.set(isInter);
    this.isIntraStateSelected.set(isIntra);

    if (isInter) {
      this.businessInformationForm.controls["mcNumber"].enable();
    } else {
      this.businessInformationForm.controls["mcNumber"].disable();
      this.businessInformationForm.controls["mcNumber"].reset();
    }

    if (isIntra) {
      this.businessInformationForm.controls["caNumber"].enable();
    } else {
      this.businessInformationForm.controls["caNumber"].disable();
      this.businessInformationForm.controls["caNumber"].reset();
    }
  }

}
